import {
  CareerSiteFiltersOption,
  CareerSiteOpeningV2Response,
} from 'api-clients/monolith';
import { createContext, Dispatch, SetStateAction } from 'react';

/**
 * Active filters and parameters that affect job search results.
 * All of these parameters are sent to the backend as URL parameters.
 */
export interface ActiveFiltersV2 {
  category: CareerSiteFiltersOption[];
  city: CareerSiteFiltersOption[];
  country: CareerSiteFiltersOption[];
  jobHours: CareerSiteFiltersOption[];
  jobType: CareerSiteFiltersOption[];
  position: CareerSiteFiltersOption[];
  locationName: CareerSiteFiltersOption[];
  state: CareerSiteFiltersOption[];
}

export interface Segment {
  id: string | null;
  value: string | null;
}

export interface FilterCounts {
  jobTypes: Array<{ id: string; count: number }>;
  jobHours: Array<{ id: string; count: number }>;
  countries: Array<{ id: string; count: number }>;
  states: Array<{ id: string; count: number }>;
  cities: Array<{ id: string; count: number }>;
  category: Array<{ id: string; count: number }>;
  position: Array<{ id: string; count: number }>;
  locationName: Array<{ id: string; count: number }>;
}

export type AppContextData = {
  accountSlug: string | undefined;
  brandId: string | undefined;
  brandName: string;
  radius: CareerSiteFiltersOption | null;
  setRadius: Dispatch<SetStateAction<CareerSiteFiltersOption | null>>;
  sortBy: CareerSiteFiltersOption;
  setSortBy: Dispatch<SetStateAction<CareerSiteFiltersOption>>;
  activeFiltersV2: ActiveFiltersV2;
  setActiveFiltersV2: Dispatch<SetStateAction<ActiveFiltersV2>>;
  location: Segment;
  setLocation: Dispatch<SetStateAction<Segment>>;
  displayLocation: string | null;
  setDisplayLocation: Dispatch<SetStateAction<string | null>>;
  position: Segment;
  setPosition: Dispatch<SetStateAction<Segment>>;
  categoryName: string | null;
  setCategoryName: Dispatch<SetStateAction<string | null>>;
  experienceLevelName: string | null;
  setExperienceLevelName: Dispatch<SetStateAction<string | null>>;
  setBrandDetails: (
    accountSlug: string,
    brandId: string | undefined,
    brandName: string,
  ) => void;
  placeId: string | null;
  setPlaceId: Dispatch<SetStateAction<string | null>>;
  jobSearchQuery: string | null;
  setJobSearchQuery: Dispatch<SetStateAction<string | null>>;
  isFetchingJobs: boolean;
  setIsFetchingJobs: Dispatch<SetStateAction<boolean>>;
  resetFilters: () => void;
  page: number;
  setPage: Dispatch<SetStateAction<number>>;
  isMobile: boolean;
  isJobsFromCurrentLocation: boolean;
  setIsJobsFromCurrentLocation: Dispatch<SetStateAction<boolean>>;
  setDefaultFilterData: (data: ActiveFiltersV2) => void;
  updateUrlWithParams: (
    accountSlug: string,
    brandId: string | undefined,
    key: string,
    value: string | string[],
  ) => void;
  replaceUrlWithParams: (
    accountSlug: string,
    brandId: string | undefined,
    key: string,
    value: string | string[],
  ) => void;
  resetUrlParams: (accountSlug: string, brandId: string) => void;
  currentLanguage: string;
  setCurrentLanguage: Dispatch<SetStateAction<string>>;
  // Jobs state
  jobs: CareerSiteOpeningV2Response['openings'];
  setJobs: Dispatch<SetStateAction<CareerSiteOpeningV2Response['openings']>>;
  paginationData: CareerSiteOpeningV2Response['pagination'] | undefined;
  setPaginationData: Dispatch<
    SetStateAction<CareerSiteOpeningV2Response['pagination'] | undefined>
  >;
  currentCity: string | null;
  setCurrentCity: Dispatch<SetStateAction<string | null>>;
  currentState: string | null;
  setCurrentState: Dispatch<SetStateAction<string | null>>;
  currentCountry: string | null;
  setCurrentCountry: Dispatch<SetStateAction<string | null>>;
  filterCounts: FilterCounts | null;
  setFilterCounts: Dispatch<SetStateAction<FilterCounts | null>>;
  isCurrentLocation: boolean;
  isFiltersDrawerOpen: boolean;
  setIsFiltersDrawerOpen: Dispatch<SetStateAction<boolean>>;
  pendingFiltersV2: ActiveFiltersV2;
  setPendingFiltersV2: Dispatch<SetStateAction<ActiveFiltersV2>>;
};

export const getDefaultActiveFilters = (): ActiveFiltersV2 => ({
  category: [],
  city: [],
  country: [],
  jobHours: [],
  jobType: [],
  position: [],
  locationName: [],
  state: [],
});

export const defaultAppContext: AppContextData = {
  accountSlug: undefined,
  brandId: undefined,
  brandName: 'abc',
  radius: null,
  setRadius: () => undefined,
  sortBy: { key: 'date_posted', label: 'Date Posted' },
  setSortBy: () => undefined,
  activeFiltersV2: {
    category: [],
    city: [],
    country: [],
    jobHours: [],
    jobType: [],
    position: [],
    locationName: [],
    state: [],
  },
  setActiveFiltersV2: () => undefined,
  location: { id: null, value: null },
  setLocation: () => undefined,
  displayLocation: null,
  setDisplayLocation: () => undefined,
  position: { id: null, value: null },
  setPosition: () => undefined,
  categoryName: null,
  setCategoryName: () => undefined,
  experienceLevelName: null,
  setExperienceLevelName: () => undefined,
  setBrandDetails: () => undefined,
  placeId: null,
  setPlaceId: () => undefined,
  jobSearchQuery: null,
  setJobSearchQuery: () => undefined,
  isFetchingJobs: false,
  setIsFetchingJobs: () => undefined,
  resetFilters: () => undefined,
  page: 1,
  setPage: () => undefined,
  isMobile: false,
  isJobsFromCurrentLocation: true,
  setIsJobsFromCurrentLocation: () => undefined,
  setDefaultFilterData: () => undefined,
  updateUrlWithParams: () => undefined,
  replaceUrlWithParams: () => undefined,
  resetUrlParams: () => undefined,
  currentLanguage: 'en',
  setCurrentLanguage: () => undefined,
  jobs: [],
  setJobs: () => undefined,
  paginationData: undefined,
  setPaginationData: () => undefined,
  currentCity: null,
  setCurrentCity: () => undefined,
  currentState: null,
  setCurrentState: () => undefined,
  currentCountry: null,
  setCurrentCountry: () => undefined,
  filterCounts: null,
  setFilterCounts: () => undefined,
  isCurrentLocation: true,
  isFiltersDrawerOpen: false,
  setIsFiltersDrawerOpen: () => undefined,
  pendingFiltersV2: getDefaultActiveFilters(),
  setPendingFiltersV2: () => undefined,
};

export const FILTER_PILL_FIELDS = [
  'category',
  'city',
  'country',
  'jobHours',
  'jobType',
  'position',
  'locationName',
  'state',
] as const;

export const AppContext = createContext<AppContextData>(defaultAppContext);
